import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Clock, Plus, X } from 'lucide-react';
import { Abas } from '../../componentes/abas';
import { Botao } from '../../componentes/base';
import type { VisaoCalendario } from '../../componentes/calendario';
import { AreaSensivel, BuscaRemota, CampoData, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { email, nomeParceiro, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CHAVE, diaLocal, useRecursos, useServicos } from './dados';
import { HorarioEditor } from './HorarioEditor';
import { FormReuniaoLivre } from './FormReuniaoLivre';
import { ehReuniaoLivre, ESTADOS_ABERTOS, type Agendamento, type HorarioTrabalho } from './tipos';
import { VisaoAgenda } from './VisaoAgenda';

/**
 * CRM no novo desenho (lote 1, C-E): página Reuniões/Agenda do clone do RD sobre o agendamento (sem tabela nova). Agendar é a
 * reunião livre (ficha 2026-11-03, `FormReuniaoLivre`); "Configurar horários" é o horário de trabalho do próprio profissional.
 * Lote crm8 (item 5): a lista deu lugar à visualização da Agenda.
 */
const DURACOES = ['15', '30', '45', '60', '90', '120'] as const;
const HORARIOS = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);

// Rlote-10 (SPEC-M1): `acoesDaReuniao` mora em `tipos.ts` (o detalhe do agendamento também usa); segue exportada daqui.
export { acoesDaReuniao } from './tipos';

export type ContatoDaReuniao = { chave: string; parceiro_id: string | null; nome: string; email: string | null };

/** L5 (C23): contatos da reunião, o principal (`parceiro_id`) primeiro e depois os convidados que são contatos ou e-mails de fora. */
export function contatosDaReuniao(a: Pick<Agendamento, 'parceiro_id' | 'parceiro_nome' | 'convidados'>): ContatoDaReuniao[] {
  const saida: ContatoDaReuniao[] = a.parceiro_nome || a.parceiro_id ? [{ chave: `p:${a.parceiro_id}`, parceiro_id: a.parceiro_id, nome: a.parceiro_nome ?? '', email: null }] : [];
  for (const c of a.convidados) {
    if (c.tipo === 'interno') continue;
    if (c.tipo === 'contato') {
      if (c.parceiro_id !== a.parceiro_id) saida.push({ chave: `p:${c.parceiro_id}`, parceiro_id: c.parceiro_id, nome: c.nome, email: c.email });
    } else saida.push({ chave: `e:${c.email.toLowerCase()}`, parceiro_id: null, nome: c.nome ?? '', email: c.email });
  }
  return saida;
}

const VISOES = ['semana', 'dia', 'mes'] as const;

/**
 * Lote crm8 (item 5, ficha 2026-10-29): Reuniões/Agenda na visualização da página Agenda (Semana, a padrão, Dia e Mês), a
 * MESMA visão (`VisaoAgenda`: o calendário do núcleo, o filtro por profissional e os bloqueios, inclusive os que vêm do
 * Google) sobre os mesmos agendamentos: uma fonte só. O cabeçalho continua o do CRM ("Configurar horários" e "Agendar
 * reunião", o formulário livre); clicar num horário abre o formulário já no dia e hora, e o detalhe ganha o "Editar reunião"
 * (a reunião livre no formulário livre; a que tem serviço no `FormReuniao`). A visão escolhida fica no endereço (`?visao=dia`).
 */
export function Reunioes() {
  const { t } = useTranslation('agenda');
  const s = useSessao();
  const qc = useQueryClient();
  const idAbas = useId();
  const [params, setParams] = useSearchParams();
  const pedida = params.get('visao');
  const visao: VisaoCalendario = (VISOES as readonly string[]).includes(pedida ?? '') ? (pedida as VisaoCalendario) : 'semana';
  const [novo, setNovo] = useState<{ inicio: Date | null } | null>(null);
  const [editando, setEditando] = useState<Agendamento | null>(null);
  const [horarios, setHorarios] = useState(false);
  const recarregar = () => qc.invalidateQueries({ queryKey: [CHAVE] }).then(() => qc.invalidateQueries({ queryKey: ['/agenda/agendamentos'] }));
  const mudarVisao = (v: VisaoCalendario) => {
    const p = new URLSearchParams(params);
    if (v === 'semana') p.delete('visao');
    else p.set('visao', v);
    setParams(p, { replace: true });
  };
  return (
    <div data-pagina-crm="reunioes" data-visao-reunioes={visao}>
      <header className="mb-5 flex flex-wrap items-start gap-3">
        <div className="min-w-48 flex-1">
          <h1 className="text-2xl font-bold tracking-tight">{t('crm.titulo')}</h1>
          <p className="mt-1 text-sm text-tinta-2">{t('crm.subtitulo')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Botao icone={<Clock className="size-4" />} onClick={() => setHorarios(true)} data-configurar-horarios="">
            {t('crm.configurarHorarios')}
          </Botao>
          {s.pode('agenda.criar') && (
            <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo({ inicio: null })} data-agendar-reuniao="">
              {t('crm.agendar')}
            </Botao>
          )}
        </div>
      </header>
      <div className="mb-4">
        <Abas grupo={idAbas} rotulo={t('crm.titulo')} ativa={visao} aoMudar={mudarVisao} itens={VISOES.map((v) => ({ chave: v, rotulo: t(`abas.${v}`) }))} />
      </div>
      <VisaoAgenda
        key={visao}
        visao={visao}
        semAcaoPrimaria
        reuniaoCrm
        aoNovo={s.pode('agenda.criar') ? (inicio) => setNovo({ inicio }) : undefined}
        aoEditar={s.pode('agenda.editar') ? setEditando : undefined}
      />
      {novo && <FormReuniaoLivre inicio={novo.inicio} aoFechar={() => setNovo(null)} />}
      {editando && (ehReuniaoLivre(editando) ? <FormReuniaoLivre atual={editando} aoFechar={() => setEditando(null)} /> : <FormReuniao atual={editando} aoFechar={() => setEditando(null)} aoSalvar={() => void recarregar()} />)}
      {horarios && <ConfigurarHorarios aoFechar={() => setHorarios(false)} />}
    </div>
  );
}

/** HH:MM de 24 h, na hora local (a do fuso de quem usa, o mesmo do `diaLocal`). */
const horaLocal = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

/**
 * "Editar reunião" do agendamento com serviço (assunto, contatos e observações; data e hora pelo remarcar, que mantém a duração).
 * Criar reunião é o formulário livre (`FormReuniaoLivre`, ficha 2026-11-03); responsável, duração e serviço aqui só se leem.
 */
export function FormReuniao({ atual, aoFechar, aoSalvar }: { atual: Agendamento; aoFechar: () => void; aoSalvar: () => void }) {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const id = useId();
  const recursos = useRecursos();
  const servicos = useServicos();
  const profissionais = useMemo(() => (recursos.data ?? []).filter((r) => r.tipo === 'profissional' && r.usuario_id), [recursos.data]);
  const [assunto, setAssunto] = useState(atual.assunto ?? '');
  // Rodada rlote-4 (ROB-M1): a reunião existente abre no dia e na hora locais (não no dia UTC do ISO nem no formato de hora do
  // idioma, que em inglês vira "10:00 PM"), sempre em HH:MM de 24 h.
  const diaInicial = diaLocal(new Date(atual.inicio));
  const horarioInicial = horaLocal(new Date(atual.inicio));
  const [dia, setDia] = useState(diaInicial);
  const [horario, setHorario] = useState(horarioInicial);
  // Rodada rlote-5 (ROB-B1): a reunião fora da meia hora (remarcada no Google, por exemplo às 10:15) mostra o horário real entre
  // as opções, na ordem, em vez de "Selecione" (quem achasse que precisa escolher remarcaria sem querer).
  const opcoesHorario = horarioInicial && !HORARIOS.includes(horarioInicial) ? [...HORARIOS, horarioInicial].sort() : HORARIOS;
  // Val5 (29/10): mudar a data de uma reunião existente é remarcar (`agenda.cancelar`); sem ela, a data fica como está.
  // Rodada rlote-11 (ROB-B2): na reunião realizada ou não ocorrida, data e hora ficam como estão (o servidor não remarca reunião
  // encerrada; mudar o horário é "Voltar para agendada" e remarcar): o Editar grava só assunto, contatos e observações.
  const travaHorario = !s.pode('agenda.cancelar') || !ESTADOS_ABERTOS.includes(atual.estado);
  const do_recurso = (servicos.data ?? []).filter((x) => x.agendavel !== false && (x.recurso_ids.length === 0 || x.recurso_ids.includes(atual.recurso_id)));
  const servicoEfetivo = atual.servico_id ?? '';
  const [obs, setObs] = useState(atual.observacoes ?? '');
  // L5 (C23): um campo só de contatos; o primeiro é o contato principal (`parceiro_id`), os demais viram convidados. Contato sem e-mail vale.
  const [contatos, setContatos] = useState<ContatoDaReuniao[]>(() => contatosDaReuniao(atual));
  const [buscaContato, setBuscaContato] = useState(0);
  // rcrm4 VIS-B1: a busca é remontada a cada escolha (limpa o texto); depois de escolher ou tirar um chip, o foco volta a ela.
  const focarBusca = useRef(false);
  useEffect(() => {
    if (!focarBusca.current) return;
    focarBusca.current = false;
    document.getElementById(`${id}-contato`)?.focus();
  }, [buscaContato, contatos, id]);
  const convidados = () => [
    ...atual.convidados.filter((c) => c.tipo === 'interno').map((c) => ({ tipo: 'interno' as const, usuario_id: c.usuario_id })),
    ...contatos.slice(1).map((c) => (c.parceiro_id ? { tipo: 'contato' as const, parceiro_id: c.parceiro_id, nome: c.nome, email: c.email } : { tipo: 'externo' as const, email: c.email ?? '', nome: c.nome || null })),
  ];
  const incluirContato = (item?: Record<string, unknown>) => {
    focarBusca.current = true;
    setBuscaContato((n) => n + 1);
    if (!item) return;
    const id = String(item.id ?? '');
    if (!id || contatos.some((c) => c.parceiro_id === id)) return;
    const em = typeof item.email === 'string' && item.email.trim() ? item.email.trim() : null;
    setContatos((l) => [...l, { chave: `p:${id}`, parceiro_id: id, nome: String(item.nome ?? ''), email: em }]);
  };
  const [erros, setErros] = useState<Record<string, string>>({});
  const [ocupado, setOcupado] = useState(false);
  const obrigatorio = tn('erros.obrigatorio');
  const salvar = async () => {
    const e: Record<string, string> = {};
    if (!dia) e.dia = obrigatorio;
    if (!horario) e.horario = obrigatorio;
    setErros(e);
    if (Object.keys(e).length) return;
    setOcupado(true);
    try {
      const inicio = new Date(`${dia}T${horario}`).toISOString();
      await patch(`/agenda/agendamentos/${atual.id}`, { assunto: assunto.trim() || null, observacoes: obs.trim() || null, convidados: convidados() });
      // Rodada rlote-4 (ROB-M1): só remarca quando a pessoa mudou o dia ou o horário.
      if (dia !== diaInicial || horario !== horarioInicial) await post(`/agenda/agendamentos/${atual.id}/remarcar`, { inicio });
      toast.sucesso(t('crm.atualizada'));
      aoSalvar();
      aoFechar();
    } catch (err) {
      toast.erro(mensagemDeErro(err));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <Modal
      aberto
      largura="lg"
      aoFechar={aoFechar}
      titulo={t('crm.editar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={ocupado} onClick={() => void salvar()} data-salvar-reuniao="">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Rotulo id={`${id}-assunto`} rotulo={t('crm.campos.assunto')}>
          {/* Rodada rlote-8: assunto e observações com a máscara dos nomes dos contatos da reunião (modo discreto). */}
          <EntradaSensivel mascara={null} livre nomesNoTexto={contatos.map((c) => c.nome)} id={`${id}-assunto`} value={assunto} maxLength={200} placeholder={t('crm.campos.assuntoAjuda')} onChange={(e) => setAssunto(e.target.value)} />
        </Rotulo>
        <Rotulo id={`${id}-resp`} rotulo={t('crm.campos.responsavel')}>
          <Selecao id={`${id}-resp`} value={atual.recurso_id} disabled vazio={tn('comum.selecione')} opcoes={profissionais.map((r) => ({ valor: r.id, rotulo: nomePessoa(r.nome) }))} />
        </Rotulo>
        <div className="grid gap-4 sm:grid-cols-3">
          <Rotulo id={`${id}-dia`} rotulo={t('crm.campos.data')} obrigatorio erro={erros.dia}>
            <CampoData id={`${id}-dia`} type="date" value={dia} disabled={travaHorario} onChange={(e) => setDia(e.target.value)} />
          </Rotulo>
          <Rotulo id={`${id}-hora`} rotulo={t('crm.campos.horario')} obrigatorio erro={erros.horario}>
            <Selecao id={`${id}-hora`} value={horario} disabled={travaHorario} onChange={(e) => setHorario(e.target.value)} vazio={tn('comum.selecione')} opcoes={opcoesHorario.map((h) => ({ valor: h, rotulo: h }))} />
          </Rotulo>
          <Rotulo id={`${id}-dur`} rotulo={t('crm.campos.duracao')}>
            <Selecao id={`${id}-dur`} value={String(atual.duracao_min)} disabled opcoes={DURACOES.map((d) => ({ valor: d, rotulo: t(`crm.duracoes.${d}`) }))} />
          </Rotulo>
        </div>
        <Rotulo id={`${id}-contato`} rotulo={t('crm.contatos')} ajuda={t('crm.contatosAjuda')}>
          <div className="flex flex-col gap-2" data-campo-contatos="">
            {contatos.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {contatos.map((c, n) => {
                  const rotulo = c.parceiro_id ? nomeParceiro(c.nome) : c.nome ? nomePessoa(c.nome) : email(c.email);
                  const fixo = n === 0;
                  return (
                    <li key={c.chave} className="inline-flex items-center gap-1 rounded-full border border-borda bg-sup-2 py-0.5 pr-1 pl-2.5 text-sm" title={n === 0 ? t('crm.contatoPrincipal') : undefined}>
                      <span className={n === 0 ? 'font-medium' : undefined}>{rotulo}</span>
                      {!c.email && c.parceiro_id && <span className="text-xs text-tinta-2">{t('crm.semEmailChip')}</span>}
                      {!fixo && (
                        <button type="button" className="grid size-6 place-items-center rounded-full text-tinta-2 hover:bg-sup-3" aria-label={t('crm.removerContato', { nome: rotulo })} onClick={() => ((focarBusca.current = true), setContatos((l) => l.filter((x) => x.chave !== c.chave)))}>
                          <X className="size-3.5" aria-hidden />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            <BuscaRemota key={buscaContato} id={`${id}-contato`} def={{ caminho: '/cadastros/parceiros', rotulo: (i) => String(i.nome) }} valor={null} placeholder={t('crm.buscarContato')} aoMudar={(_v, item) => incluirContato(item)} />
          </div>
        </Rotulo>
        <Rotulo id={`${id}-servico`} rotulo={t('crm.campos.servico')}>
          <Selecao id={`${id}-servico`} value={servicoEfetivo} disabled opcoes={do_recurso.map((x) => ({ valor: x.id, rotulo: x.nome }))} vazio={tn('comum.selecione')} />
        </Rotulo>
        <Rotulo id={`${id}-obs`} rotulo={t('crm.campos.observacoes')} ajuda={`${obs.length}/1000`}>
          <AreaSensivel nomesNoTexto={contatos.map((c) => c.nome)} id={`${id}-obs`} rows={5} maxLength={1000} placeholder={t('crm.campos.observacoesAjuda')} value={obs} onChange={(e) => setObs(e.target.value)} />
        </Rotulo>
      </div>
    </Modal>
  );
}

/** "Configurar horários": o horário de trabalho do profissional da própria pessoa (cria o profissional ao salvar, se não tem). */
function ConfigurarHorarios({ aoFechar }: { aoFechar: () => void }) {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [CHAVE, 'meu-horario'], queryFn: () => get<{ recurso: { id: string; horario_trabalho: HorarioTrabalho } | null; padrao: HorarioTrabalho }>('/agenda/meu-horario') });
  const [horario, setHorario] = useState<HorarioTrabalho | null>(null);
  const valor = horario ?? q.data?.recurso?.horario_trabalho ?? q.data?.padrao ?? {};
  const salvar = useMutation({
    mutationFn: () => api('PUT', '/agenda/meu-horario', { horario_trabalho: valor }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [CHAVE] });
      toast.sucesso(t('crm.horarios.salvos'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={t('crm.configurarHorarios')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={salvar.isPending} disabled={q.isPending} onClick={() => salvar.mutate()} data-salvar-horarios="">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <h3 className="text-base font-semibold">{t('crm.horarios.titulo')}</h3>
      <p className="mt-1 mb-4 text-sm text-tinta-2">{t('crm.horarios.texto')}</p>
      {!q.isPending && <HorarioEditor valor={valor} aoMudar={setHorario} />}
    </Drawer>
  );
}
